import { Alert, Button, Card, Flex, Layout, Spin, Typography } from "antd"
import React, { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"
import { Link, useLocation, useNavigate } from "react-router-dom"

import Images from "../Images"
import AddEmailForm from "../components/Forms/AddEmailForm"
import SignUpEmailForm from "../components/Forms/SignUpEmailForm"
import PrivacyPolicy from "../components/PrivacyPolicy"
import { generateDocumentTitle } from "../constants/document-title"
import { RoutesPaths } from "../constants/routes"
import services from "../services/api"
import { type RootState } from "../store"
import { pickFormError } from "../utils/handlers/pick-form-error"
import { useQuery } from "../utils/utils"
import { authErrorReset } from "./LoginPage/redux/auth.slice"
import { createAccount } from "./LoginPage/redux/auth.thunks"
import { fetchInviteTeamMemberInfo } from "./TeamManagementPage/redux/team-members.thunks"

const { Title, Paragraph } = Typography

type SignUpFormData = Record<string, unknown>

function SignUpPage({
  //isMobile,
  auth: {
    error,
    fetching,
    userInfo,
    accessToken,
    refreshToken
    //isAdmin
  },
  inviteInfoFetching,
  inviteInfo,
  createAccount,
  getInviteUserInfo,
  resetError
}: ConnectedProps<typeof connector>) {
  const { search, state } = useLocation()
  const query = useQuery(search)
  const invite = query.get("invite")

  const navigate = useNavigate()
  const { hash } = useParams()
  const { t } = useTranslation()

  const initialValues =
    !!userInfo && state === "change_email"
      ? {
          first_name: userInfo.first_name,
          last_name: userInfo.last_name,
          email: userInfo.email
        }
      : null

  function handleSubmit(data: SignUpFormData) {
    createAccount({
      data: {
        ...data,
        ...(invite && { invite })
      },
      navigate
    })
  }

  // A customer already signed in (an existing account accepting a SECOND
  // GC's invitation) must never be bounced away before the invite is
  // acted on — the server proves identity by session, not by a password
  // this page would otherwise ask them to retype, so this fires the same
  // accept-invite request with no form at all. Guarded by a ref rather
  // than state so a re-render from the resulting `fetching`/`error`
  // change can't re-fire it.
  const acceptedReturningInviteRef = useRef(false)
  useEffect(() => {
    if (!userInfo || !invite || acceptedReturningInviteRef.current) return
    acceptedReturningInviteRef.current = true
    // `createAccount` posts through this module's OWN apisauce instance
    // (`services`), separate from the one the rest of the app authenticates
    // with — it only ever carries a token from a signup/login that happened
    // through this exact instance. A session rehydrated from a page load
    // (exactly the case here: click an invite link, land already logged in)
    // never synced it, so the request would go out unauthenticated and the
    // server would wrongly answer "please sign in" to someone who already is.
    if (accessToken) services.updateAccessToken(accessToken, refreshToken)
    createAccount({ data: { invite }, navigate })
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- an invite is accepted once, when the signed-in account and the invite are both known; the ref above guards a repeat
  }, [userInfo, invite])

  useEffect(() => {
    if (hash) localStorage.setItem("ref_link_info", hash)
    if (invite) getInviteUserInfo(invite)
    if (userInfo) {
      // A pending invite is handled by the effect above instead — it
      // still needs the account's own identity (email, whether it needs
      // to add one) resolved first for every OTHER branch here.
      if (invite) return
      if (userInfo?.["email_added"]) {
        navigate(RoutesPaths.home + (search || ""))
      } else {
        if (userInfo?.["email_added"]) {
          if (userInfo?.["is_email_verified"]) {
            navigate("/onboarding/business-info")
          } else {
            if (initialValues) return
            navigate("/check-mailbox")
          }
        } else {
          console.log("add-email")
          navigate("/add-email")
        }
      }
    } else {
      generateDocumentTitle("Sign Up")
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- runs once when the sign-up page opens, with the link and the session as they are then
  }, [])

  const isAcceptingReturningInvite = !!userInfo && !!invite

  // `error` is reset (below) the moment it arrives, so a message meant to
  // stay on screen until the user acts on it (the sign-in/wrong-session
  // cases) has to be captured into local state on the way past rather
  // than read live off the fast-clearing redux value.
  const [returningInviteError, setReturningInviteError] = React.useState<{
    type: string
    message: string | null
  } | null>(null)
  useEffect(() => {
    if (!error || typeof error !== "object" || !("type" in error)) return
    const type = (error as { type?: string }).type
    if (
      type === "account_exists_sign_in_required" ||
      type === "account_exists_wrong_session"
    ) {
      setReturningInviteError({ type, message: pickFormError(error) as string })
    }
  }, [error])

  useEffect(() => {
    if (error) resetError()
  }, [error, resetError])

  return (
    <Layout className={"min-h-svh auth-bg"}>
      <Spin size="large" spinning={fetching || inviteInfoFetching}>
        <Flex
          vertical
          align="center"
          justify="center"
          className={"min-h-svh md:pt-14"}
        >
          {/*<LoginHeader />*/}

          <Card
            className="login-card shadow-sm border-gray-200/70"
            style={{ margin: "auto", width: "100%", maxWidth: "520px" }}
          >
            <Flex vertical align="center" style={{ marginBottom: 16 }}>
              <div className={"flex items-center gap-4"}>
                <img src={Images.logo} alt="Logo" className="h-16 md:h-20" />
                {/*<Flex vertical>*/}
                {/*  <Text className="font-semibold text-2xl md:text-4xl !leading-relaxed">*/}
                {/*    EZBuild*/}
                {/*  </Text>*/}
                {/*  <Text className={"text-sm md:text-lg font-semibold"}>*/}
                {/*    Simple. Easy. Smart.*/}
                {/*  </Text>*/}
                {/*</Flex>*/}
              </div>
              {isAcceptingReturningInvite ? (
                <>
                  <Title level={1} style={{ marginBottom: 6 }}>
                    {t("Accepting invitation")}
                  </Title>
                  {returningInviteError?.type ===
                  "account_exists_wrong_session" ? (
                    <Alert
                      className="mt-4 w-full"
                      type="warning"
                      showIcon
                      message={returningInviteError.message}
                    />
                  ) : (
                    <Paragraph
                      type="secondary"
                      className={"text-center"}
                      style={{ fontSize: 16, margin: 0 }}
                    >
                      {t("Adding this company to your account…")}
                    </Paragraph>
                  )}
                </>
              ) : !userInfo?.["email_added"] && Boolean(userInfo) ? (
                <>
                  <Title level={1} style={{ marginBottom: 6 }}>
                    {t("Enter Your Email")}
                  </Title>
                  <AddEmailForm
                    onSubmit={handleSubmit}
                    error={error?.message}
                  />
                </>
              ) : (
                <>
                  <Title level={1} style={{ marginBottom: 6 }}>
                    {/* An invitation knows who it was sent to, so greet them
                        rather than ask a named person to create an account. */}
                    {inviteInfo?.name
                      ? `${t("Welcome")}, ${inviteInfo.name}`
                      : t("Create an account")}
                  </Title>
                  <Paragraph
                    type="secondary"
                    className={"text-center"}
                    style={{ fontSize: 16, margin: 0 }}
                  >
                    {t("Let’s set up your EZBuild account.")}
                  </Paragraph>

                  {returningInviteError?.type ===
                    "account_exists_sign_in_required" && (
                    <Alert
                      className="mt-4 w-full"
                      type="info"
                      showIcon
                      message={returningInviteError.message}
                      action={
                        <Link to={`${RoutesPaths.auth.login}?invite=${invite}`}>
                          <Button size="small">{t("Sign In")}</Button>
                        </Link>
                      }
                    />
                  )}

                  <div className={"pt-4 w-full"}>
                    <SignUpEmailForm
                      {...(initialValues && { initialValues })}
                      {...(invite && inviteInfo && { inviteInfo })}
                      isInvite={!!invite}
                      onSubmit={handleSubmit}
                      // The two returning-invite cases already have their
                      // own clear Alert above; this form's own error
                      // banner would otherwise show a second, unhelpful
                      // generic message for the exact same failure.
                      error={returningInviteError ? null : pickFormError(error)}
                    />
                  </div>
                </>
              )}
            </Flex>
          </Card>

          <PrivacyPolicy />
        </Flex>
      </Spin>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
  isMobile: state.nav.isMobile,
  inviteInfoFetching: state.users.inviteInfoFetching,
  inviteInfo: state.users.inviteInfo
})

// wraps dispatch to create nicer functions to call within our component
const mapDispatchToProps = {
  createAccount: createAccount,
  resetError: authErrorReset,
  getInviteUserInfo: fetchInviteTeamMemberInfo
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(SignUpPage)
